import Button from "@/common/elements/Button";
import Select from "@/common/elements/InputElements/Select";
import TextField from "@/common/elements/InputElements/TextField";
import FormItem from "@/common/elements/InputElements/FormItem.jsx";
import Table from "@/common/elements/Table";
import { StyledFlex, StyledGridCol, StyledGridRow } from "@/common/styles";
import { FieldSecurityProvider } from "@/contexts/FieldSecurityContext";
import SecureFormItem from "@/common/SecureFormItem";
import {
  InmateDetailsTableContainer,
  TableHeader,
} from "@/components/InmateDetails/styles";
import emptyImage from "@/assets/emptyImage.svg";
import Modal from "@/common/elements/Modal";
import { forwardRef, useEffect, useImperativeHandle, useState } from "react";
import { Form, Popconfirm } from "antd";
import FileUploader from "@/components/FileUploader";
import {
  getFinalPath,
  getOptionsFromRefList,
  getRefListItemGlobalIdByDefaultString,
  getRefListItemsbyName,
  hasCheckSecurity,
  isNonEmptyVariable,
} from "@/util/common_function";
import API_ENDPOINTS, { PHOTOS_BASE_URL } from "@/util/api";
import api from "@/services/axios_custom";
import { v4 as uuidV4 } from "uuid";
import { useDispatch, useSelector } from "react-redux";
import { setDeleteAPis } from "@/slices/bookingsSlice";
import { Entity_types, Security_Types } from "@/util/permission_bits";
import { LABELS_CAPITAL } from "@/util/usePermissionMask";
import {
  MODULES_LIST_WITH_ID,
  SUB_MODULES_LIST_WITH_ID,
} from "@/util/common_variable";

const DistinguishingCharacteristicsForm = forwardRef(
  (
    {
      formData,
      isQuickAdd = false,
      quickSave,
      bookingsFieldsEnabled,
      isEdit = false,
      startingPoint = "",
    },
    ref
  ) => {
    const dispatch = useDispatch();
    const [form] = Form.useForm();
    const [photoForm] = Form.useForm();
    const [imgModal, setImgModal] = useState(false);
    const [isImgUploading, setIsImgUploading] = useState(false);
    const [uploadedImage, setUploadedImage] = useState(null);
    const [imgData, setImgData] = useState([]);
    const [selectedImg, setSelectedImg] = useState(null);
    const [isImgEdit, setIsImgEdit] = useState(false);
    const [locationOptions, setLocationOptions] = useState([]);
    const fullSearch = useSelector((state) => state.fullSearch);
    const { refListItemContexts } = useSelector((state) => state?.refListReducer);

    // -----------------------------------------------------------------------
    // Sync external formData → antd form
    // -----------------------------------------------------------------------
    useEffect(() => {
      if (isNonEmptyVariable(formData)) {
        form.setFieldsValue(formData);
        if (
          isNonEmptyVariable(formData?.distinguishingCharacteristicsPhotos) &&
          formData?.distinguishingCharacteristicsPhotos?.length
        ) {
          const di = formData.distinguishingCharacteristicsPhotos.map((d) => ({
            ...d?.photos?.[0],
            uuid: uuidV4(),
          }));
          setImgData(di);
        } else {
          setImgData([]);
        }
      } else {
        form.resetFields();
      }

      if (!isEdit) {
        setImgData([]);
        setSelectedImg(null);
      }
    }, [formData, isEdit]);

    // -----------------------------------------------------------------------
    // Expose form API to parent via ref
    // -----------------------------------------------------------------------
    useImperativeHandle(ref, () => ({
      validateFields: () => form.validateFields(),
      resetForm: () => form.resetFields(),
      getImgData: () => imgData,
      setImgDataEmpty: () => setImgData([]),
      setSelectedImgEmpty: () => setSelectedImg(null),
    }));

    // -----------------------------------------------------------------------
    // Photo modal handlers
    // -----------------------------------------------------------------------
    const handleImgModal = () => {
      photoForm.resetFields();
      setImgModal((prev) => !prev);
    };

    const handleFileUpload = (file) => {
      if (file) {
        setUploadedImage(file);
      } else {
        console.error("No file selected or invalid file format");
      }
    };

    const onSaveClick = () => {
      if (!uploadedImage) return;

      const payload = new FormData();
      payload.append("file", uploadedImage);
      payload.append("description", photoForm.getFieldValue("description"));
      payload.append("imageType", "Mug-Front");

      setIsImgUploading(true);
      api
        .post(API_ENDPOINTS.imageUpload, payload, {
          headers: { "Content-Type": "multipart/form-data" },
        })
        .then(({ data }) => {
          const description = photoForm.getFieldValue("description");
          setImgData((prev) => [...prev, { ...data.body, description }]);
          // setImgData((prev) => [...prev, data.body]);
          handleImgModal();
        })
        .catch((error) => console.error("Image upload failed:", error))
        .finally(() => {
          setIsImgUploading(false);
          photoForm.resetFields();
        });
    };

    const handleDelete = () => {
      const filtered = imgData.filter((d) => d?.uuid !== selectedImg?.uuid);
      setImgData(filtered);

      if (isNonEmptyVariable(selectedImg?.photoId)) {
        dispatch(setDeleteAPis(`${API_ENDPOINTS.bioPhotos}/id/${selectedImg.photoId}`));
      }
    };

    // -----------------------------------------------------------------------
    // Characteristic Type -> Location dependent options
    // -----------------------------------------------------------------------
    const getDependentOptions = (contextValue, targetRefListName) => {
      if (!contextValue || !refListItemContexts) return [];
      const matched = refListItemContexts.filter(
        (item) => item.contextID == contextValue
      );

      if (!matched.length) return [];
      const ids = matched.map((m) => m.refListItemGlobalID);
      return getOptionsFromRefList(targetRefListName, false, false, false, "", "", ids);
    };

    const handleCharacteristicTypeChange = (value) => {
      const refitems = getRefListItemsbyName("DistinguishingCharacteristics");
      const selectedItem = refitems?.find(
        (item) => item.defaultString === value
      );
      const globalId = selectedItem?.refListItemGlobalId;
      // Location no longer applies to the new type, so its dependent NIST
      // lookup (attribute lives on the Location item) is invalidated too.
      form.setFieldsValue({ location: undefined, nist: undefined });
      const options = getDependentOptions(globalId, "CharacteristicLocation");
      setLocationOptions(options);
    };

    // -----------------------------------------------------------------------
    // NIST auto-fill — mirrors legacy ASP CharacteristicTypeOrLocationChanged
    // (NCIC2000 lookup mode): the NIST code is stored as a RefListItemAttrib
    // on the selected Location item, named "NCIC2000_<Characteristic Type>".
    // -----------------------------------------------------------------------
    const fetchAndSetNist = async (typeValue, locationValue) => {
      if (!typeValue || !locationValue) {
        form.setFieldsValue({ nist: undefined });
        return;
      }

      const locationGlobalId = getRefListItemGlobalIdByDefaultString(
        "CharacteristicLocation",
        locationValue
      );
      if (!locationGlobalId) {
        form.setFieldsValue({ nist: undefined });
        return;
      }

      try {
        const { data } = await api.get(
          API_ENDPOINTS.config.refList.getRefListItemAttribByGlobalID +
            locationGlobalId
        );
        const attribs = Array.isArray(data) ? data : data ? [data] : [];
        const attribName = `NCIC2000_${typeValue}`.trim().toLowerCase();
        const match = attribs.find(
          (a) => a?.attribName?.trim().toLowerCase() === attribName
        );
        form.setFieldsValue({ nist: match?.attribValue ?? "" });
      } catch (error) {
        console.error("Failed to fetch NIST attribute:", error);
        form.setFieldsValue({ nist: undefined });
      }
    };

    const handleLocationChange = (value) => {
      const typeValue = form.getFieldValue("characteristicType");
      fetchAndSetNist(typeValue, value);
    };

    // -----------------------------------------------------------------------
    // Sub-module security checks (not field-level)
    // -----------------------------------------------------------------------
    const canAdd = hasCheckSecurity(
      Security_Types.functionality,
      Entity_types.subModule,
      LABELS_CAPITAL.ADD,
      MODULES_LIST_WITH_ID.MODULE_BOOKINGS,
      SUB_MODULES_LIST_WITH_ID.Arrest_Report
    );

    const canDelete = hasCheckSecurity(
      Security_Types.functionality,
      Entity_types.subModule,
      LABELS_CAPITAL.DELETE,
      MODULES_LIST_WITH_ID.MODULE_BOOKINGS,
      SUB_MODULES_LIST_WITH_ID.Arrest_Report
    );

    const canView = hasCheckSecurity(
      Security_Types.functionality,
      Entity_types.subModule,
      LABELS_CAPITAL.VIEW,
      MODULES_LIST_WITH_ID.MODULE_BOOKINGS,
      SUB_MODULES_LIST_WITH_ID.Arrest_Report
    );

    // -----------------------------------------------------------------------
    // Render
    // -----------------------------------------------------------------------
    return (
      <>
        <FieldSecurityProvider
          entity="DistinguishingCharacteristics"
          startingPoint={startingPoint}
          bookingsFieldsEnabled={bookingsFieldsEnabled}
        >
          <Form
            name="DistinguishingCharacteristics"
            form={form}
            disabled={!bookingsFieldsEnabled}
          >
            <StyledGridRow gutter={16}>

              {/* Characteristic Type */}
              <StyledGridCol span={8}>
                <SecureFormItem
                  fieldName="characteristicType"
                  formItemProps={{
                    name: "characteristicType",
                    label: "Characteristic Type",
                    required: !fullSearch,
                    rules: !fullSearch
                      ? [{ required: true, message: "Characteristic Type is required!" }]
                      : [],
                  }}
                >
                  <Select
                    options={getOptionsFromRefList("DistinguishingCharacteristics")}
                    onChange={handleCharacteristicTypeChange}
                    placeholder="Characteristic Type"
                    showSearch
                    variant="outlined"
                  />
                </SecureFormItem>
              </StyledGridCol>

              {/* Location */}
              <StyledGridCol span={8}>
                <SecureFormItem
                  fieldName="location"
                  formItemProps={{
                    name: "location",
                    label: "Location",
                    required: !fullSearch,
                    rules: !fullSearch
                      ? [{ required: true, message: "Location is required!" }]
                      : [],
                  }}
                >
                  <Select
                    options={
                      locationOptions.length
                        ? locationOptions
                        : getOptionsFromRefList("CharacteristicLocation")
                    }
                    onChange={handleLocationChange}
                    placeholder="Location"
                    showSearch
                    variant="outlined"
                  />
                </SecureFormItem>
              </StyledGridCol>

              {/* NIST */}
              <StyledGridCol span={8}>
                <SecureFormItem
                  fieldName="nist"
                  formItemProps={{ name: "nist", label: "NIST" }}
                >
                  <TextField />
                </SecureFormItem>
              </StyledGridCol>

              {/* Description */}
              <StyledGridCol flex="auto">
                <SecureFormItem
                  fieldName="description"
                  formItemProps={{ name: "description", label: "Description" }}
                >
                  <TextField inputType="textarea" />
                </SecureFormItem>
              </StyledGridCol>

              {/* Q-Add button — sub-module security, not field-level */}
              {isQuickAdd && (
                <StyledGridCol style={{ paddingTop: "30px" }}>
                  <Button
                    disabled={!bookingsFieldsEnabled || !canAdd}
                    onClick={quickSave}
                  >
                    Q-Add
                  </Button>
                </StyledGridCol>
              )}

            </StyledGridRow>
          </Form>
        </FieldSecurityProvider>

        {/* Photos section — outside FieldSecurityProvider intentionally,
            these buttons use sub-module security not field-level */}
        {!isQuickAdd && (
          <>
            <InmateDetailsTableContainer>
              <TableHeader>
                <h6>List of photos</h6>

                <StyledFlex justify="end" gap="10px" style={{ marginBottom: "10px" }}>
                  <Button
                    disabled={!bookingsFieldsEnabled || !canAdd}
                    onClick={handleImgModal}
                  >
                    Add Photo
                  </Button>

                  <Popconfirm
                    title="Delete the photos"
                    description="Are you sure to delete this photo?"
                    onConfirm={handleDelete}
                    onCancel={() => {}}
                    okText="Yes"
                    cancelText="No"
                  >
                    <Button disabled={!bookingsFieldsEnabled || !canDelete}>
                      Delete
                    </Button>
                  </Popconfirm>
                </StyledFlex>
              </TableHeader>

              <StyledGridRow gutter={10}>
                <StyledGridCol span={16}>
                  {canView && (
                    <Table
                      headers={[
                        { key: "description", text: "Description" },
                        { key: "imageType", text: "Image Type" },
                      ]}
                      data={imgData}
                      onRowClick={setSelectedImg}
                    />
                  )}
                </StyledGridCol>

                <StyledGridCol span={8}>
                  <img
                    src={
                      isNonEmptyVariable(selectedImg)
                        ? PHOTOS_BASE_URL + getFinalPath(selectedImg?.fullSizePath)
                        : emptyImage
                    }
                    width="100%"
                    style={{ border: "1px solid rgba(0,0,0,0.1)" }}
                  />
                </StyledGridCol>
              </StyledGridRow>
            </InmateDetailsTableContainer>

            {/* Photo upload modal — uses its own plain Form, no field security needed */}
            <Modal
              size=""
              header="Photo Information"
              style={{ minWidth: "580px" }}
              show={imgModal}
              onHide={handleImgModal}
              footerActions={[
                {
                  key: "save",
                  text: isImgEdit ? "Update" : "Save",
                  onClick: onSaveClick,
                  disabled: isImgUploading,
                  isLoading: isImgUploading,
                },
              ]}
              footerCloseAction
              footerCloseActionText="Cancel"
            >
              <Form form={photoForm}>
                <StyledGridRow>
                  <StyledGridCol span={24}>
                    <FormItem name="description" label="Description">
                      <TextField inputType="textarea" />
                    </FormItem>
                  </StyledGridCol>
                  <StyledGridCol span={24}>
                    <FileUploader
                      browseFileType="image"
                      acceptImageFormats="image/png,image/jpeg,image/jpg"
                      allowMultiple={false}
                      onUpload={handleFileUpload}
                      imageUrlFromParent={
                        PHOTOS_BASE_URL +
                        getFinalPath(selectedImg?.photos?.[0]?.fullSizePath)
                      }
                    />
                  </StyledGridCol>
                </StyledGridRow>
              </Form>
            </Modal>
          </>
        )}
      </>
    );
  }
);

export default DistinguishingCharacteristicsForm;